@let subTotalPrice = subTotalPrice$ | async | finPrice;
@let currentStep = currentStep$ | async;

<fin-subview
  #subview
  [subviewName]="subviewName"
  [subviewContext]="subviewContext"
  [enableScroll]="(basketOpen$ | async) === false"
  (closeSubViewEvent)="close.emit($event)"
  (shouldCloseSubview)="onClose()"
  [ancillaryLoader$]="ancillaryLoader$"
  data-testid="ancillary-subview"
>
  <ng-container ngProjectAs="header">
    <fin-cms-global-disruption-banner />
    <fin-sub-header
      [showInfoIcon]="currentStep?.phase === STEPS.ANCILLARIES.phase || currentStep?.phase === STEPS.BAGS.phase"
      [hasBasket]="true"
      [isBasketDisabled]="false"
      [hasActiveBasketState]="false"
      [hasBasketActionInMobileOnly]="true"
      [backgroundColorClass]="'nordic-blue-900-bg surface-contrast'"
      (openBasket)="openBasket()"
    >
      <fcom-button
        #closeButton
        [theme]="ButtonTheme.DARK_TERTIARY_GHOST"
        [attr.title]="backButtonTextKey | finLocalization"
        [disabled]="disableButtons$ | async"
        [text]="backButtonTextKey | finLocalization"
        [icon]="{
          name: SvgLibraryIcon.ARROW_LEFT,
          position: IconPosition.LEFT,
        }"
        data-testid="close-subview-button"
        class="close-button"
        (clickBtn)="closeSubview()"
      />
    </fin-sub-header>
  </ng-container>

  <ng-container ngProjectAs="main-content">
    <div class="subview-content-sub-wrapper grey-25-bg bg-primary" #scrollContainer>
      <main class="grid-max-width grid-col-12 grow--min">
        <section
          class="grey-25-bg bg-primary"
          [ngClass]="{
            'col-1-12 md-col-2-8': showShoppingCart(),
            'subview-content col-f md-col-1-9 sm-col-1-8': !showShoppingCart(),
          }"
        >
          <section class="inner-grid-content relative" [class.shopping-cart]="showShoppingCart()">
            <!-- Show the content only when slideIndex could be established -->
            @if (boundsOrFlights.length > 0 && slideIndex >= 0) {
              <fin-ancillary-header [title]="titleKey | finLocalization">
                @if (subtitleMessageKey$ | async; as messageKey) {
                  <span class="font-body-1 font-body-medium">{{ messageKey | finLocalization }}</span>
                }
                <fin-ancillary-flight-bound-selector
                  class="pr-large-t mobile-bleed"
                  [showFlightCard$]="showFlightCard$"
                  [boundsOrFlights]="boundsOrFlights"
                  [passengers]="passengers$ | async"
                  [activeBoundIndex]="slideIndex"
                  (selectIndex)="updateSlideIndex($event, false)"
                  [locations]="locations"
                  (viewDetails)="viewDetails()"
                  [focusOnDetails$]="itineraryModalClosed$"
                />
              </fin-ancillary-header>
            }

            <div class="overflow-hidden mobile-bleed">
              <div class="slider nowrap fill slide-{{ slideIndex }}">
                <ng-content />
              </div>
            </div>

            <div #ctaButtonHeightFixRef></div>
          </section>
        </section>
        @if (showDesktopCart()) {
          <aside class="shopping-cart-wrapper md-col-9-12">
            <fin-shopping-cart></fin-shopping-cart>
          </aside>
        } @else if (!showShoppingCart()) {
          <aside
            class="subview-cart grow sm-col-9-12 md-col-10-12 grey-50-bg surface-disabled"
            [class.open]="basketOpen$ | async"
          >
            <fin-booking-summary
              [unpaidServices]="unpaidServices$ | async"
              [currentStep]="currentStep$ | async"
              [onlyShowAncillaries]="onlyShowAncillaries"
              [flowContext]="subviewContext"
              [hideChangeButton]="hideChangeButton"
              (close)="closeBasket()"
              (edit)="onEdit($event)"
            />
          </aside>
        }
      </main>
    </div>
  </ng-container>

  <ng-container ngProjectAs="global-content">
    <fcom-cta-button
      data-testid="common-booking-ancillaries-subview-cta-button"
      *finLet="showFlightCard$ | async as showFlightCard"
      [visible]="(ancillaryLoader$ | async) === AncillaryLoaderStatus.OFF"
      [title]="ctaButtonTitle | finLocalization"
      [position]="isShoppingCartMobileView ? ButtonPosition.GRID_FILL_MOBILE : ButtonPosition.GRID"
      [disabled]="disableButtons$ | async"
      [isTabletUp]="isTabletUp$ | async"
      [removeContentPadding]="isShoppingCartMobileView"
      [showSecondaryButton]="!hideCheckoutButton"
      [secondaryButtonDisabled]="secondaryButtonDisabled$ | async"
      [secondaryButtonTitle]="
        ((isNonChargeableAncillary$ | async) === true
          ? 'services.productCards.confirm'
          : 'services.productCards.goToReview'
        ) | finLocalization
      "
      (secondaryButtonClicked)="handleSecondaryCtaClick(showFlightCard)"
      (buttonClicked)="handleCtaClick(showFlightCard)"
    >
      @if (isShoppingCartMobileView) {
        <div
          data-testid="mobile-shopping-cart-summary"
          class="nordic-blue-50-bg surface-secondary nordic-blue-900-text text-icon-primary flex flex--space-between ps-small font-body-1 elevation-level-2--inverted font-body-medium"
        >
          <div>
            <fcom-icon [name]="SvgLibraryIcon.SHOPPING_CART"></fcom-icon>
            <fcom-link
              data-testid="mobile-shopping-cart-summary-button"
              class="ps-small-l"
              (beforeNavigation)="openShoppingCart()"
              >{{ 'shoppingCart.viewYourCart' | finLocalization }}</fcom-link
            >
          </div>
          <div>
            @if (subTotalPrice) {
              <span>{{ 'subtotal' | finLocalization }}</span>
              <span data-testid="mobile-shopping-cart-summary-subtotal" class="bold-type">
                {{ subTotalPrice }}
              </span>
            }
          </div>
        </div>
      } @else {
        <div>
          @if (!showFlightCard) {
            <span class="gray-50-text">{{ 'subtotal' | finLocalization }}: </span>
            <span data-testid="common-booking-ancillaries-subview-price" class="font-body-1 font-body-medium">{{
              subTotalPrice | finEmpty: '&ndash;'
            }}</span>
          } @else {
            <span class="flex flex--center">
              <span class="flex-1 flex--basis0">
                <span class="gray-50-text">{{ 'subtotal' | finLocalization }}: </span>
                <span data-testid="common-booking-ancillaries-subview-price" class="font-body-1 font-body-medium">{{
                  subTotalPrice | finEmpty: '&ndash;'
                }}</span>
                @if (
                  (category === Category.SEAT || category === Category.SEAT_WIFI) && (reassignFreeSeatsVisible$ | async)
                ) {
                  <div class="flex flex--column">
                    <button data-testid="reassign-seat-button" class="underline" (click)="reassignFreeSeats()">
                      <b>{{ 'ancillaries.seats.reassignFreeSeat' | finLocalization }}</b>
                      <fcom-icon class="icon-medium" [name]="SvgLibraryIcon.ARROW_BACK" />
                    </button>
                  </div>
                }
              </span>
            </span>
          }
        </div>
      }
    </fcom-cta-button>

    @if (category !== Category.COVER) {
      <fin-itinerary-modal [itineraryModal]="itineraryModal" (closed)="itineraryModalClosed$.emit($event)" />
    }
    @if (confirmModalData$ | async; as confirmModalData) {
      @if (confirmModalData?.confirmData?.confirmationModalTranslationKeys; as confirmationModalTranslationKeys) {
        <fcom-modal
          data-testid="ancillary-subview-confirmation-modal"
          [open]="confirmModalData.open"
          [fitToContent]="true"
          [title]="confirmationModalTranslationKeys.title | finLocalization"
          [buttons]="ModalButtons.CUSTOM"
          (close)="closeConfirmModal.emit()"
          [id]="'confirmationModal'"
        >
          <div class="mr-large-x mr-small-y font-body-1 nordic-blue-900-text text-icon-primary font-body-medium">
            <p
              [innerHTML]="confirmationModalTranslationKeys.firstContentParagraph | finLocalization | finSafe: 'html'"
            ></p>
            <p class="mr-xsmall-t">{{ confirmationModalTranslationKeys.secondContentParagraph | finLocalization }}</p>
          </div>
          <div buttons>
            <fcom-button
              [theme]="ButtonTheme.LIGHT_SECONDARY_OUTLINE"
              (clickBtn)="closeConfirmModal.emit()"
              [text]="confirmationModalTranslationKeys.closeButton | finLocalization"
              [aria]="{ label: 'actions.cancel' | finLocalization, role: 'button' }"
              class="mr-xsmall-r"
              data-testid="confirm-modal-close"
            />
            <fcom-button
              (clickBtn)="closeConfirmModal.emit(confirmModalData.confirmData)"
              [text]="confirmationModalTranslationKeys.continueButton | finLocalization"
              [aria]="{ label: 'actions.continue' | finLocalization, role: 'button' }"
              class="mr-xsmall-r"
              data-testid="confirm-modal-continue"
            />
          </div>
        </fcom-modal>
      }
    }
  </ng-container>
</fin-subview>
